// Tiny IndexedDB-backed store for the last-known unread notifications count.
// Persisted so the app icon badge can still be shown when a count fetch fails.
// Usable from both window and Service Worker contexts (IndexedDB is available in
// both, unlike localStorage). Every operation is error-guarded and never throws.

const DB_NAME = 'mdrt-app-badge';
const DB_VERSION = 1;
const STORE_NAME = 'badge';
const UNREAD_COUNT_KEY = 'unread-count';

const isIndexedDatabaseSupported = (): boolean =>
  typeof indexedDB !== 'undefined' && indexedDB !== null;

const openDatabase = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (!isIndexedDatabaseSupported()) {
      resolve(null);

      return;
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.addEventListener('upgradeneeded', () => {
        const database = request.result;

        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      });

      request.addEventListener('success', () => resolve(request.result));
      request.addEventListener('error', () => resolve(null));
      request.addEventListener('blocked', () => resolve(null));
    } catch {
      resolve(null);
    }
  });

export const getStoredUnreadCount = async (): Promise<null | number> => {
  const database = await openDatabase();

  if (!database) return null;

  return new Promise<null | number>((resolve) => {
    try {
      const transaction = database.transaction(STORE_NAME, 'readonly');
      const request = transaction.objectStore(STORE_NAME).get(UNREAD_COUNT_KEY);

      request.addEventListener('success', () => {
        const value = request.result;
        resolve(typeof value === 'number' ? value : null);
      });
      request.addEventListener('error', () => resolve(null));
      transaction.addEventListener('complete', () => database.close());
    } catch {
      resolve(null);
    }
  });
};

export const setStoredUnreadCount = async (count: number): Promise<void> => {
  const database = await openDatabase();

  if (!database) return;

  await new Promise<void>((resolve) => {
    try {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put(count, UNREAD_COUNT_KEY);

      transaction.addEventListener('complete', () => {
        database.close();
        resolve();
      });
      transaction.addEventListener('error', () => resolve());
      transaction.addEventListener('abort', () => resolve());
    } catch {
      resolve();
    }
  });
};
